import React from "react";
import ContactForm from "../components/ContactForm";
import PixelIcon from "../components/PixelIcon";
import SceneVideo from "../components/SceneVideo";
import BrandIcon from "../components/BrandIcon";
import { Reveal } from "../components/motion/Reveal";

const HERO_STATS = [
  { value: "4", label: "Products live" },
  { value: "3", label: "App stores" },
  { value: "AI · SaaS · Web3", label: "Focus" },
];

const MARQUEE_ITEMS = [
  "Privateer",
  "ChattyBooks",
  "Full Groove Radio",
  "Farty Unicorn",
  "LLM Systems",
  "Computer Vision",
  "Solana",
  "SaaS Platforms",
  "Mobile Apps",
  "Generative Media",
];

const ABOUT_HIGHLIGHTS = [
  {
    icon: "spark",
    title: "AI-Powered Solutions",
    body: "Language models, computer vision, and intelligent automation integrated into real products.",
  },
  {
    icon: "chain",
    title: "Web3 & Blockchain",
    body: "On-chain systems, smart contracts, and token-based economies built for production.",
  },
  {
    icon: "stack",
    title: "Scalable Architecture",
    body: "Cloud-native infrastructure designed to handle growth from day one.",
  },
  {
    icon: "rocket",
    title: "Full-Stack Execution",
    body: "From product design through deployment and operations — end to end.",
  },
];

const WHY_POINTS = [
  {
    title: "Product-first mindset",
    body: "We think in outcomes and user value, not billable hours or feature checklists.",
  },
  {
    title: "AI & blockchain fluency",
    body: "Deep, production-level experience with the technologies that matter.",
  },
  {
    title: "End-to-end execution",
    body: "From architecture and design through deployment, monitoring, and iteration.",
  },
  {
    title: "Modern scalable stacks",
    body: "React, Node, AWS, Solana, and AI/ML tooling — chosen for the problem, not the trend.",
  },
  {
    title: "Founder-level involvement",
    body: "Senior engineers on every project. No handoffs to junior teams.",
  },
];

// Client engagements shipped to production (update as the count grows).
const CLIENT_PROJECTS_SHIPPED = 27;

const CLIENT_WORK = [
  "SaaS platforms",
  "AI integrations",
  "Smart contracts & Web3",
  "iOS & Android apps",
  "Internal tools & dashboards",
  "Marketing sites",
];

const SERVICES = [
  {
    icon: "window",
    title: "SaaS Product Development",
    body: "Full-stack design, development, and deployment of subscription-based software platforms from zero to production.",
  },
  {
    icon: "spark",
    title: "AI & Machine Learning Solutions",
    body: "Custom AI integrations, language model applications, computer vision systems, and intelligent automation pipelines.",
  },
  {
    icon: "chain",
    title: "Blockchain & Web3 Development",
    body: "Smart contracts, token economies, on-chain applications, and decentralized system architecture.",
  },
  {
    icon: "target",
    title: "Technical Consulting",
    body: "Architecture reviews, technology selection, scaling strategies, and hands-on technical advisory for product teams.",
  },
  {
    icon: "arrow",
    title: "Product & Technical Marketing",
    body: "Go-to-market strategy, technical content, developer documentation, and product positioning for technical audiences.",
  },
];

const HomePage = () => {
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="hero" id="hero" aria-label="Introduction">
        <div className="hero-stage">
          <iframe
            src="/motion/"
            title="Hayden Bulk Tech Labs — we build digital products that actually ship"
            allow="autoplay"
            loading="eager"
          />
        </div>
        <div className="container hero-inner">
          <Reveal mount delay={0.08}>
            <div className="hero-eyebrow">
              <p className="section-label">Product-Driven Tech Studio</p>
              <button
                type="button"
                className="status-pill"
                onClick={() => scrollToSection("products")}
              >
                <span className="status-dot" aria-hidden="true" />
                Privateer is live on iOS, Android &amp; Seeker
              </button>
            </div>
          </Reveal>
          {/* the animation says it on screen; keep the real heading for SEO & screen readers */}
          <h1 className="visually-hidden">
            We Build Digital Products That Actually Ship
          </h1>
          <Reveal mount delay={0.16} className="hero-foot">
            <p className="hero-sub">
              SaaS platforms, AI systems, and blockchain applications —
              designed, built, and scaled by a product-first engineering
              studio.
            </p>
            <dl className="hero-stats">
              {HERO_STATS.map((stat) => (
                <div className="hero-stat" key={stat.label}>
                  <dt>{stat.label}</dt>
                  <dd>{stat.value}</dd>
                </div>
              ))}
            </dl>
            <div className="hero-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => scrollToSection("contact")}
              >
                Book a Technical Consultation
                <span className="btn-arrow" aria-hidden="true">&rarr;</span>
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => scrollToSection("products")}
              >
                Explore Our Products
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== MARQUEE ===== */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span className="marquee-item" key={i}>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ===== ABOUT ===== */}
      <section className="about" id="about" aria-label="About Hayden Bulk Tech Labs">
        <div className="container">
          <div className="bento">
            <Reveal className="bento-tile bento-intro">
              <p className="section-label">About Us</p>
              <h2>A Studio That Ships Production Systems</h2>
              <p>
                Hayden Bulk Tech Labs is a product-driven technology studio
                focused on building real, revenue-ready digital products.
              </p>
              <p>
                We don't just consult — we design, ship, iterate, and operate
                production systems across AI, SaaS, blockchain, and mobile
                platforms.
              </p>
              <p>
                Our work spans content platforms, Web3 games and
                on-chain systems, virtual try-on and computer vision products,
                and consumer and B2B SaaS applications.
              </p>
            </Reveal>
            <Reveal className="bento-tile bento-stat" delay={0.06}>
              <span className="bento-stat-num">04</span>
              <span className="bento-stat-label">
                Products live in production, built and operated in-house
              </span>
            </Reveal>
            {ABOUT_HIGHLIGHTS.map((item, i) => (
              <Reveal
                className="bento-tile about-highlight"
                key={item.title}
                delay={0.1 + i * 0.06}
              >
                <div className="service-icon" aria-hidden="true">
                  <PixelIcon name={item.icon} size={18} />
                </div>
                <h4>{item.title}</h4>
                <p>{item.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRODUCTS ===== */}
      <section className="products" id="products" aria-label="Our products">
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Products</p>
            <h2>What We've Built</h2>
            <p className="section-intro">
              Production systems serving real users — not prototypes or pitch
              decks.
            </p>
          </Reveal>

          <div className="product-stack">
            {/* Privateer */}
            <Reveal className="product-card" style={{ "--stack-index": 0 }}>
              <span className="product-index" aria-hidden="true">
                01<small>/04</small>
              </span>
              <div className="product-logo" style={{ background: "#fff" }}>
                <img
                  src="/privateer-logo.png"
                  alt="Privateer logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>Privateer</h3>
                <p className="product-tagline">
                  Chart your own course, privately.
                </p>
                <p className="product-desc">
                  A privacy-first AI platform with encrypted conversations and
                  zero-data-retention inference. Talk to frontier models,
                  generate images and video, sketch on the whiteboard, build
                  shareable Cargo, and code from the CLI agent — without your
                  data ever becoming the product. Available now on Android,
                  iOS, and the Solana Seeker dApp Store.
                </p>
                <ul className="product-features">
                  <li>Encrypted conversations, zero data retention</li>
                  <li>Multi-model access to frontier AI</li>
                  <li>Image &amp; video generation</li>
                  <li>Whiteboard + Cargo builder</li>
                  <li>CLI coding agent</li>
                  <li>Developer API</li>
                </ul>
                {/* TODO: swap App Store / Solana dApp Store URLs for direct store links */}
                <div className="store-badges">
                  <a
                    className="store-badge"
                    href="https://play.google.com/store/apps/details?id=app.privateer"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get Privateer on Google Play"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="googleplay" size={18} />
                    </span>
                    <span>
                      <small>Get it on</small>
                      <strong>Google Play</strong>
                    </span>
                  </a>
                  <a
                    className="store-badge"
                    href="https://privateer.pro"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download Privateer on the App Store"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="apple" size={18} />
                    </span>
                    <span>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </span>
                  </a>
                  <a
                    className="store-badge"
                    href="https://privateer.pro"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get Privateer on the Solana dApp Store"
                  >
                    <span className="store-badge-icon">
                      <BrandIcon name="solana" size={16} />
                    </span>
                    <span>
                      <small>Available on the</small>
                      <strong>Solana dApp Store</strong>
                    </span>
                  </a>
                </div>
                <div className="product-meta">
                  <a
                    href="https://privateer.pro"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Privateer
                  </a>
                  <span className="product-platform">
                    Android &middot; iOS &middot; Solana Seeker &middot; Web
                  </span>
                </div>
              </div>
            </Reveal>

            {/* ChattyBooks */}
            <Reveal className="product-card" style={{ "--stack-index": 1 }}>
              <span className="product-index" aria-hidden="true">
                02<small>/04</small>
              </span>
              <div className="product-logo" style={{ background: "#000" }}>
                <img
                  src="/chattybook-logo.png"
                  alt="ChattyBooks logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>ChattyBooks</h3>
                <p className="product-tagline">
                  AI-Powered Interactive Reading Platform
                </p>
                <p className="product-desc">
                  An AI-driven platform that transforms the reading experience
                  into an interactive, personalized journey. ChattyBooks uses
                  language models to generate adaptive content, enabling users to
                  engage with stories in ways that static text never could.
                </p>
                <ul className="product-features">
                  <li>AI-generated interactive narratives</li>
                  <li>Personalized reading experiences</li>
                  <li>Real-time content adaptation</li>
                  <li>Multi-format story support</li>
                  <li>Intelligent content recommendations</li>
                  <li>Cross-platform accessibility</li>
                </ul>
                <div className="product-meta">
                  <a
                    href="https://www.chattybooks.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit ChattyBooks
                  </a>
                  <span className="product-platform">Web Platform</span>
                </div>
              </div>
            </Reveal>

            {/* Full Groove Radio */}
            <Reveal className="product-card" style={{ "--stack-index": 2 }}>
              <span className="product-index" aria-hidden="true">
                03<small>/04</small>
              </span>
              <div className="product-logo" style={{ background: "#000" }}>
                <img
                  src="/fullgrooveradio-logo.png"
                  alt="Full Groove Radio logo"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "4px" }}
                />
              </div>
              <div className="product-content">
                <h3>Full Groove Radio</h3>
                <p className="product-tagline">
                  Fully Automated AI Media Generation Pipeline
                </p>
                <p className="product-desc">
                  An experimental, fully automated daily media generation
                  pipeline building a fictional world through AI-generated
                  audio, video, and imagery distributed across YouTube, TikTok,
                  Instagram, and the web — every day, without human
                  intervention.
                </p>
                <ul className="product-features">
                  <li>Daily AI-generated audio and video content</li>
                  <li>Multi-platform automated distribution</li>
                  <li>Persistent fictional world-building</li>
                  <li>End-to-end generative media pipeline</li>
                  <li>Cross-format narrative continuity</li>
                  <li>Zero-touch publishing workflow</li>
                </ul>
                <div className="social-links" style={{ marginBottom: "20px" }}>
                  <a
                    className="social-link"
                    href="https://www.youtube.com/@FullGrooveRadio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="youtube" size={14} />
                    YouTube
                  </a>
                  <a
                    className="social-link"
                    href="https://www.instagram.com/fullgrooveradio/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="instagram" size={14} />
                    Instagram
                  </a>
                  <a
                    className="social-link"
                    href="https://www.tiktok.com/@fullgrooveradio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BrandIcon name="tiktok" size={14} />
                    TikTok
                  </a>
                </div>
                <div className="product-meta">
                  <a
                    href="https://www.fullgrooveradio.com"
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Full Groove Radio
                  </a>
                  <span className="product-platform">Web / YouTube / TikTok / Instagram</span>
                </div>
              </div>
            </Reveal>

            {/* Farty Unicorn */}
            <Reveal className="product-card" style={{ "--stack-index": 3 }}>
              <span className="product-index" aria-hidden="true">
                04<small>/04</small>
              </span>
              <div className="product-logo" style={{ background: "#fff" }}>
                <img
                  src="/farty-unicorn.png"
                  alt="Farty Unicorn mascot"
                  width="72"
                  height="72"
                  loading="lazy"
                  style={{ objectFit: "contain", padding: "6px" }}
                />
              </div>
              <div className="product-content">
                <h3>Farty Unicorn</h3>
                <p className="product-tagline">
                  Blockchain-Powered Mobile Game
                </p>
                <p className="product-desc">
                  A Web3 mobile game that combines engaging gameplay with
                  blockchain-based mechanics. Farty Unicorn features on-chain
                  assets, token-based rewards, and decentralized game economies
                  — proving that blockchain gaming can be fun and accessible.
                  Available now on the Solana Seeker dApp Store.
                </p>
                <ul className="product-features">
                  <li>On-chain game assets and NFTs</li>
                  <li>Token-based reward system</li>
                  <li>Decentralized game economy</li>
                  <li>Cross-platform mobile gameplay</li>
                  <li>Smart contract integration</li>
                  <li>Community-driven development</li>
                </ul>
                {/* TODO: no public per-app dApp Store URL — make this a link if one becomes available */}
                <div className="store-badges">
                  <span className="store-badge store-badge-static">
                    <span className="store-badge-icon">
                      <BrandIcon name="solana" size={16} />
                    </span>
                    <span>
                      <small>Available on the</small>
                      <strong>Solana dApp Store</strong>
                    </span>
                  </span>
                </div>
                <div className="product-meta">
                  <span className="product-platform">Mobile &middot; Web3 &middot; Solana Seeker</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="services" id="services" aria-label="Our services">
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Services</p>
            <h2>What We Do</h2>
            <p className="section-intro">
              End-to-end technical capabilities for companies building digital
              products.
            </p>
          </Reveal>

          <div className="services-list">
            {SERVICES.map((service, i) => (
              <Reveal
                className="service-row"
                key={service.title}
                delay={i * 0.05}
              >
                <span className="service-num" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="service-icon" aria-hidden="true">
                  <PixelIcon name={service.icon} size={18} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CLIENT WORK ===== */}
      <section className="clients" id="clients" aria-label="Client work">
        <div className="container clients-grid">
          <Reveal className="clients-count">
            <span className="clients-num">{CLIENT_PROJECTS_SHIPPED}</span>
            <span className="clients-unit">client projects shipped</span>
          </Reveal>
          <Reveal className="clients-copy" delay={0.08}>
            <p className="section-label">Client Work</p>
            <h2>Not just our own products</h2>
            <p className="section-intro">
              Alongside our in-house products, we&apos;ve designed, built, and
              launched software for startups and established teams — and
              stayed on to support it after launch.
            </p>
            <ul className="clients-kinds">
              {CLIENT_WORK.map((kind) => (
                <li key={kind}>{kind}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="why-us" id="why-us" aria-label="Why work with us">
        <SceneVideo
          src="/built-different.mp4"
          poster="/built-different-poster.jpg"
          className="scene-video--why"
        />
        <div className="container">
          <Reveal className="why-head">
            <p className="section-label">Why Us</p>
            <h2>Built Different</h2>
            <p className="section-intro">
              We operate like a product company, not an agency. Every
              engagement is treated like our own product launch.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ol className="why-list">
              {WHY_POINTS.map((point, i) => (
                <li key={point.title}>
                  <span className="why-num" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <strong>{point.title}</strong>
                  <span className="why-body">{point.body}</span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* ===== ENGAGEMENT MODELS ===== */}
      <section
        className="engagement"
        id="engagement"
        aria-label="Engagement models"
      >
        <div className="container">
          <Reveal className="section-header">
            <p className="section-label">Engagement Models</p>
            <h2>How We Work</h2>
            <p className="section-intro">
              Flexible structures designed for different stages and scopes.
            </p>
          </Reveal>

          <div className="engagement-grid">
            <Reveal className="engagement-card" delay={0}>
              <div className="engagement-num">01</div>
              <h3>Fixed-Scope MVP Builds</h3>
              <p>
                Defined scope, timeline, and deliverables. Ideal for validating
                a product concept and getting to market.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.06}>
              <div className="engagement-num">02</div>
              <h3>Monthly Retainers</h3>
              <p>
                Ongoing development capacity for teams that need consistent
                engineering support and iteration cycles.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.12}>
              <div className="engagement-num">03</div>
              <h3>Technical Advisory</h3>
              <p>
                Strategic guidance on architecture, AI integration, blockchain
                strategy, and technology decisions.
              </p>
            </Reveal>
            <Reveal className="engagement-card" delay={0.18}>
              <div className="engagement-num">04</div>
              <h3>Co-Development Partnerships</h3>
              <p>
                Shared ownership and equity-based partnerships for founders
                building ambitious technical products.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== CONTACT FORM ===== */}
      <ContactForm />
    </>
  );
};

export default HomePage;
